import React, { memo, useState, useEffect, useMemo } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { getContacts, updateContact, searchLinkedInActivity, searchNewsMentions } from '../../services/radar';

const RadarBuyerCards = memo(({ 
  accounts = [],
  contacts: initialContacts = [],
  selectedCompany = '',
  onCompanyChange = null,
  contactSearchTerm = '',
  onContactSearchChange = null
}) => {
  const { showError, showSuccess, showWarning } = useToast();
  const [contacts, setContacts] = useState(initialContacts);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null); // { contactId, field }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingReportees, setEditingReportees] = useState(null); // contactId when editing reportees
  const [selectedReportees, setSelectedReportees] = useState([]);
  const [editingTeamMembers, setEditingTeamMembers] = useState(null); // contactId when editing team members
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [refreshingLinkedIn, setRefreshingLinkedIn] = useState(null); // contactId when refreshing LinkedIn
  const [refreshingNews, setRefreshingNews] = useState(null); // contactId when refreshing news
  const [showLinkedInIdPopup, setShowLinkedInIdPopup] = useState(false); // show popup when LinkedIn ID is missing
  const [contactNeedingLinkedInId, setContactNeedingLinkedInId] = useState(null); // contact that needs LinkedIn ID

  // Load contacts when component mounts
  useEffect(() => {
    loadContacts();
  }, []);

  // Refetch contacts scoped to the selected company
  useEffect(() => {
    if (selectedCompany) {
      loadContacts(selectedCompany);
    }
  }, [selectedCompany]);

  // Update contacts when initialContacts changes
  useEffect(() => {
    if (initialContacts && initialContacts.length > 0) {
      setContacts(initialContacts);
    }
  }, [initialContacts]);

  const loadContacts = async (companyName = '') => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 10000 // fetch a large batch to avoid pagination gaps
      };

      if (companyName) {
        params.company = companyName;
      }

      const response = await getContacts(params);
      setContacts(response.contacts || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
      showError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Get unique companies from contacts and accounts
  const companies = useMemo(() => {
    const companySet = new Set();
    
    // Add companies from accounts
    accounts.forEach(account => {
      if (account.companyName) {
        companySet.add(account.companyName);
      }
    });
    
    // Add companies from contacts
    contacts.forEach(contact => {
      if (contact.company || contact.companyName) {
        companySet.add(contact.company || contact.companyName);
      }
    });
    
    return Array.from(companySet).sort();
  }, [accounts, contacts]);

  // Filter contacts by selected company and search term, then sort alphabetically
  const filteredContacts = useMemo(() => {
    if (!selectedCompany) return [];
    
    let filtered = contacts.filter(contact => {
      const contactCompany = contact.company || contact.companyName || '';
      return contactCompany === selectedCompany;
    });
    
    // Apply search filter if search term is provided
    if (contactSearchTerm && contactSearchTerm.trim()) {
      const searchLower = contactSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(contact => {
        const firstName = (contact.firstName || '').toLowerCase();
        const lastName = (contact.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const title = (contact.title || '').toLowerCase();
        const email = (contact.email || '').toLowerCase();
        const phone = (contact.phone || '').toLowerCase();
        
        return (
          fullName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          title.includes(searchLower) ||
          email.includes(searchLower) ||
          phone.includes(searchLower)
        );
      });
    }
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      
      // If names are empty, fall back to email
      const sortKeyA = nameA || a.email || '';
      const sortKeyB = nameB || b.email || '';
      
      return sortKeyA.localeCompare(sortKeyB);
    });
  }, [contacts, selectedCompany, contactSearchTerm]);

  // Helper function to get reportees for a contact
  const getReportees = (contact) => {
    const properties = contact.properties || {};
    const reportees = properties.reportees || [];
    // Sort reportees alphabetically
    return [...reportees].sort((a, b) => a.localeCompare(b));
  };

  // Helper function to get team members (peers with same manager)
  const getTeamMembers = (contact) => {
    const properties = contact.properties || {};
    const manager = properties.manager;
    
    if (!manager) return [];
    
    // Find all contacts with the same manager (excluding the contact itself)
    const teamMembers = filteredContacts.filter(c => {
      const cProps = c.properties || {};
      return cProps.manager === manager && c.id !== contact.id;
    });
    
    // Sort team members alphabetically by name
    return teamMembers.sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Helper function to get last engagement
  const getLastEngagement = (contact) => {
    const properties = contact.properties || {};
    return properties.lastEngagement || 'No recent engagement';
  };

  // Helper function to get LinkedIn activity
  const getLinkedInActivity = (contact) => {
    const properties = contact.properties || {};
    const activity = properties.linkedInActivity;
    if (!activity || activity === 'No recent activity' || activity === 'No LinkedIn activity found') {
      return null;
    }
    // Check if it's an array of activities or a string
    if (Array.isArray(activity)) {
      return activity;
    }
    // Try to parse if it's a JSON string
    try {
      const parsed = JSON.parse(activity);
      return Array.isArray(parsed) ? parsed : [activity];
    } catch {
      return [activity];
    }
  };

  // Helper function to get news mentions
  const getNewsMentions = (contact) => {
    const properties = contact.properties || {};
    const mentions = properties.newsMentions;
    if (!mentions || mentions === 'No recent mentions' || mentions === 'No news mentions found') {
      return null;
    }
    // Check if it's an array of articles or a string
    if (Array.isArray(mentions)) {
      return mentions;
    }
    // Try to parse if it's a JSON string
    try {
      const parsed = JSON.parse(mentions);
      return Array.isArray(parsed) ? parsed : [mentions];
    } catch {
      return [mentions];
    }
  };


  // Helper function to format contact name
  const getContactName = (contact) => {
    const firstName = contact.firstName || '';
    const lastName = contact.lastName || '';
    return `${firstName} ${lastName}`.trim() || contact.email || 'Unknown';
  };

  // Start editing a field
  const startEditing = (contactId, field, currentValue) => {
    setEditingField({ contactId, field });
    setEditValue(currentValue || '');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Save edited field
  const saveEdit = async (contact) => {
    if (!editingField) return;
    
    try {
      setSaving(true);
      const { field } = editingField;
      
      // Prepare update data based on field type
      let updateData = {};
      
      // Check if it's a basic field or a property field
      const basicFields = ['firstName', 'lastName', 'email', 'phone', 'title'];
      const propertyFields = ['manager', 'lastEngagement', 'linkedInActivity', 'newsMentions'];
      
      if (basicFields.includes(field)) {
        updateData[field] = editValue;
      } else if (propertyFields.includes(field)) {
        updateData.properties = {
          [field]: editValue
        };
      } else if (field === 'reportees') {
        // Handle reportees as array
        const reporteesArray = editValue.split('\n').map(r => r.trim()).filter(r => r);
        updateData.properties = {
          reportees: reporteesArray
        };
      }
      
      // Call API to update contact
      await updateContact(contact.id, updateData);
      
      // Update local state optimistically
      setContacts(prevContacts => 
        prevContacts.map(c => {
          if (c.id === contact.id) {
            if (basicFields.includes(field)) {
              return { ...c, [field]: editValue };
            } else if (field === 'reportees') {
              const reporteesArray = editValue.split('\n').map(r => r.trim()).filter(r => r);
              return {
                ...c,
                properties: {
                  ...c.properties,
                  reportees: reporteesArray
                }
              };
            } else {
              return {
                ...c,
                properties: {
                  ...c.properties,
                  [field]: editValue
                }
              };
            }
          }
          return c;
        })
      );
      
      // Clear editing state
      setEditingField(null);
      setEditValue('');
    } catch (err) {
      console.error('Error saving edit:', err);
      showError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Check if a field is being edited
  const isEditing = (contactId, field) => {
    return editingField?.contactId === contactId && editingField?.field === field;
  };

  // Start editing reportees
  const startEditingReportees = (contactId, currentReportees) => {
    setEditingReportees(contactId);
    setSelectedReportees(currentReportees || []);
  };

  // Cancel editing reportees
  const cancelEditingReportees = () => {
    setEditingReportees(null);
    setSelectedReportees([]);
  };

  // Save reportees
  const saveReportees = async (contact) => {
    try {
      setSaving(true);
      
      const updateData = {
        properties: {
          reportees: selectedReportees
        }
      };
      
      // Call API to update contact
      await updateContact(contact.id, updateData);
      
      // Update local state
      setContacts(prevContacts => 
        prevContacts.map(c => {
          if (c.id === contact.id) {
            return {
              ...c,
              properties: {
                ...c.properties,
                reportees: selectedReportees
              }
            };
          }
          return c;
        })
      );
      
      // Clear editing state
      setEditingReportees(null);
      setSelectedReportees([]);
    } catch (err) {
      console.error('Error saving reportees:', err);
      showError('Failed to save reportees. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle reportee selection
  const toggleReportee = (name) => {
    setSelectedReportees(prev => {
      if (prev.includes(name)) {
        return prev.filter(n => n !== name);
      } else {
        return [...prev, name];
      }
    });
  };

  // Start editing team members
  const startEditingTeamMembers = (contactId, currentTeamMembers, currentManager) => {
    setEditingTeamMembers(contactId);
    // Get names of current team members
    const currentNames = currentTeamMembers.map(m => getContactName(m));
    setSelectedTeamMembers(currentNames);
  };

  // Cancel editing team members
  const cancelEditingTeamMembers = () => {
    setEditingTeamMembers(null);
    setSelectedTeamMembers([]);
  };

  // Save team members
  const saveTeamMembers = async (contact) => {
    try {
      setSaving(true);
      
      const contactProps = contact.properties || {};
      const manager = contactProps.manager;
      
      // If contact doesn't have a manager, we can't set team members
      // (team members are contacts that share the same manager)
      if (!manager) {
        showWarning('This contact needs to have a manager set before you can assign team members. Team members are contacts that share the same manager.');
        return;
      }
      
      // Get current team members to find what needs to be removed
      const currentTeamMembers = getTeamMembers(contact);
      const currentNames = currentTeamMembers.map(m => getContactName(m));
      
      // Contacts to add (selected but not currently team members)
      const toAdd = selectedTeamMembers.filter(name => !currentNames.includes(name));
      // Contacts to remove (currently team members but not selected)
      const toRemove = currentNames.filter(name => !selectedTeamMembers.includes(name));
      
      // Update contacts to add as team members (set their manager to match)
      const addPromises = toAdd.map(async (teamMemberName) => {
        const teamMemberContact = filteredContacts.find(c => getContactName(c) === teamMemberName);
        if (teamMemberContact && teamMemberContact.id !== contact.id) {
          const currentProps = teamMemberContact.properties || {};
          return updateContact(teamMemberContact.id, {
            properties: {
              ...currentProps,
              manager: manager
            }
          });
        }
      });
      
      // Update contacts to remove from team members (clear or change their manager)
      const removePromises = toRemove.map(async (removedName) => {
        const removedContact = filteredContacts.find(c => getContactName(c) === removedName);
        if (removedContact) {
          const currentProps = removedContact.properties || {};
          // Remove manager property or set to different value
          const updatedProps = { ...currentProps };
          delete updatedProps.manager;
          return updateContact(removedContact.id, {
            properties: updatedProps
          });
        }
      });

      await Promise.all([...addPromises.filter(p => p), ...removePromises.filter(p => p)]);

      // Reload contacts to reflect changes
      await loadContacts();

      // Clear editing state
      setEditingTeamMembers(null);
      setSelectedTeamMembers([]);
    } catch (err) {
      console.error('Error saving team members:', err);
      showError('Failed to save team members. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle team member selection
  const toggleTeamMember = (name) => {
    setSelectedTeamMembers(prev => {
      if (prev.includes(name)) {
        return prev.filter(n => n !== name);
      } else {
        return [...prev, name];
      }
    });
  };

  // Refresh LinkedIn activity
  const refreshLinkedInActivity = async (contact) => {
    // Check if contact has LinkedIn ID
    if (!contact.linkedinId || contact.linkedinId.trim() === '') {
      // Show popup asking user to update LinkedIn ID
      setContactNeedingLinkedInId(contact);
      setShowLinkedInIdPopup(true);
      return;
    }

    try {
      setRefreshingLinkedIn(contact.id);
      
      const searchData = {
        name: getContactName(contact),
        company: contact.company || contact.companyName || selectedCompany,
        email: contact.email,
        phone: contact.phone,
        linkedinId: contact.linkedinId
      };

      const response = await searchLinkedInActivity(contact.id, searchData);

      // Check if there's an error in the response (but allow fallback to proceed)
      if (!response.success && response.error && !response.fallback) {
        showError(`LinkedIn search failed: ${response.error}`);
        return;
      }

      // Show info message if it's a fallback (using LinkedIn profile link)
      if (response.fallback && response.error) {
        console.info('LinkedIn search fallback:', response.error);
        // Don't show alert for fallback - just log it, the profile link will be displayed
      }

      // Update local state with new LinkedIn activity
      if (response.activities && response.activities.length > 0) {
        // Store as JSON string for consistency
        const activityJson = JSON.stringify(response.activities);
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contact.id) {
              return {
                ...c,
                properties: {
                  ...c.properties,
                  linkedInActivity: activityJson
                }
              };
            }
            return c;
          })
        );
      } else {
        // Set to "No LinkedIn activity found" if nothing found
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contact.id) {
              return {
                ...c,
                properties: {
                  ...c.properties,
                  linkedInActivity: 'No LinkedIn activity found'
                }
              };
            }
            return c;
          })
        );
      }
    } catch (err) {
      console.error('Error refreshing LinkedIn activity:', err);
      const errorMessage = err.message || 'Failed to refresh LinkedIn activity. Please try again.';
      showError(errorMessage);
    } finally {
      setRefreshingLinkedIn(null);
    }
  };

  // Refresh news mentions
  const refreshNewsMentions = async (contact) => {
    try {
      setRefreshingNews(contact.id);
      
      const searchData = {
        name: getContactName(contact),
        company: contact.company || contact.companyName || selectedCompany,
        email: contact.email,
        phone: contact.phone
      };

      const response = await searchNewsMentions(contact.id, searchData);

      // Check if there's an error in the response or if the request failed
      if ((response.success === false || response.error) && !response.articles?.length) {
        // Show detailed error message
        const errorMessage = response.error?.includes('OpenAI API key is not configured')
          ? 'OpenAI API key is not configured. Please configure OPENAI_API_KEY in your environment variables.'
          : response.error?.includes('Google Custom Search API is not configured')
          ? 'Google Custom Search API is not configured. Please configure GOOGLE_API_KEY and GOOGLE_CUSTOM_SEARCH_ENGINE_ID environment variables.'
          : response.error?.includes('rate limit')
          ? 'API rate limit exceeded. Please try again later.'
          : response.error || 'Failed to fetch news articles';
        
        showError(`News search failed: ${errorMessage}`);
        
        // Update contact with error message for reference
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contact.id) {
              return {
                ...c,
                properties: {
                  ...c.properties,
                  newsMentions: `Error: ${errorMessage}`
                }
              };
            }
            return c;
          })
        );
        return;
      }

      // Update local state with new news mentions
      if (response.articles && response.articles.length > 0) {
        // Store as JSON string for consistency
        const mentionsJson = JSON.stringify(response.articles);
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contact.id) {
              return {
                ...c,
                properties: {
                  ...c.properties,
                  newsMentions: mentionsJson
                }
              };
            }
            return c;
          })
        );
      } else {
        // Set to "No news mentions found" if nothing found
        setContacts(prevContacts => 
          prevContacts.map(c => {
            if (c.id === contact.id) {
              return {
                ...c,
                properties: {
                  ...c.properties,
                  newsMentions: 'No news mentions found'
                }
              };
            }
            return c;
          })
        );
      }
    } catch (err) {
      console.error('Error refreshing news mentions:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to refresh news mentions. Please try again.';
      showError(`Failed to refresh news mentions: ${errorMessage}`);
    } finally {
      setRefreshingNews(null);
    }
  };

  return (
    <div className="buyer-cards-container">
      {/* Main Content - Header section with filters removed, now in RadarHeader */}
      <div className="buyer-cards-content">
        {loading && (
          <div className="buyer-cards-loading">
            <div className="spinner"></div>
            <p>Loading contacts...</p>
          </div>
        )}

        {/* Error messages now handled by toast notifications in top right corner */}

        {!loading && !selectedCompany && (
          <div className="buyer-cards-empty-state">
            <div className="empty-state-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3>Select a Company</h3>
            <p>Choose a company from the dropdown above to view buyer information</p>
          </div>
        )}

        {!loading && selectedCompany && filteredContacts.length === 0 && (
          <div className="buyer-cards-empty-state">
            <div className="empty-state-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3>No Contacts Found</h3>
            <p>No contacts found for {selectedCompany}. Add contacts to see buyer information here.</p>
          </div>
        )}
        
        {!loading && selectedCompany && filteredContacts.length > 0 && (
          <div className="buyer-cards-cards">
            {filteredContacts.map((contact) => {
              const reportees = getReportees(contact);
              const teamMembers = getTeamMembers(contact);
              const lastEngagement = getLastEngagement(contact);
              const linkedInActivity = getLinkedInActivity(contact);
              const newsMentions = getNewsMentions(contact);

              return (
                <div key={contact.id} className="buyer-card">
                  {/* Card Header */}
                  <div className="buyer-card-header">
                    <div className="buyer-avatar">
                      {getContactName(contact).charAt(0).toUpperCase()}
                    </div>
                    <div className="buyer-card-header-info">
                      <h3 className="buyer-name">{getContactName(contact)}</h3>
                      
                      {/* Editable Title */}
                      <div className="editable-field-container">
                        {isEditing(contact.id, 'title') ? (
                          <div className="edit-mode">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="edit-input"
                              autoFocus
                            />
                            <div className="edit-actions">
                              <button 
                                onClick={() => saveEdit(contact)}
                                disabled={saving}
                                className="save-btn"
                              >
                                {saving ? '...' : '✓'}
                              </button>
                              <button 
                                onClick={cancelEditing}
                                disabled={saving}
                                className="cancel-btn"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="view-mode">
                            <p className="buyer-title">{contact.title || 'No title'}</p>
                            <button 
                              onClick={() => startEditing(contact.id, 'title', contact.title)}
                              className="edit-btn"
                              title="Edit title"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="buyer-card-body">
                    {/* Contact Info */}
                    <div className="buyer-info-section">
                      {/* Email */}
                      <div className="buyer-info-item editable-field-container">
                        <span className="info-icon">📧</span>
                        {isEditing(contact.id, 'email') ? (
                          <div className="edit-mode inline-edit">
                            <input
                              type="email"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="edit-input"
                              autoFocus
                            />
                            <div className="edit-actions">
                              <button 
                                onClick={() => saveEdit(contact)}
                                disabled={saving}
                                className="save-btn"
                              >
                                {saving ? '...' : '✓'}
                              </button>
                              <button 
                                onClick={cancelEditing}
                                disabled={saving}
                                className="cancel-btn"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="view-mode inline-view">
                            <span className="info-text">{contact.email || 'Email not available'}</span>
                            <button 
                              onClick={() => startEditing(contact.id, 'email', contact.email)}
                              className="edit-btn"
                              title="Edit email"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Phone */}
                      <div className="buyer-info-item editable-field-container">
                        <span className="info-icon">📱</span>
                        {isEditing(contact.id, 'phone') ? (
                          <div className="edit-mode inline-edit">
                            <input
                              type="tel"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="edit-input"
                              autoFocus
                            />
                            <div className="edit-actions">
                              <button 
                                onClick={() => saveEdit(contact)}
                                disabled={saving}
                                className="save-btn"
                              >
                                {saving ? '...' : '✓'}
                              </button>
                              <button 
                                onClick={cancelEditing}
                                disabled={saving}
                                className="cancel-btn"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="view-mode inline-view">
                            <span className="info-text">{contact.phone || 'Phone number not available'}</span>
                            <button 
                              onClick={() => startEditing(contact.id, 'phone', contact.phone)}
                              className="edit-btn"
                              title="Edit phone"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Reportees */}
                    <div className="buyer-section" style={{ position: 'relative' }}>
                      <div className="section-content">
                        {editingReportees === contact.id ? (
                          <div className="reportees-edit-mode" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            zIndex: 1000,
                            padding: '16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '2px solid #667eea'
                          }}>
                            <div className="multi-select-container" style={{
                              borderRadius: '8px',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: '200px',
                              maxHeight: '400px',
                              overflow: 'hidden',
                              position: 'relative',
                              boxSizing: 'border-box'
                            }}>
                              <div className="multi-select-header" style={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#374151',
                                paddingBottom: '12px',
                                marginBottom: '12px',
                                flexShrink: 0,
                                width: '100%',
                                boxSizing: 'border-box'
                              }}>
                                Select people who report to {getContactName(contact)}:
                              </div>
                              <div className="multi-select-options" style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '8px',
                                flex: '1 1 auto',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                minHeight: 0,
                                width: '100%',
                                position: 'relative',
                                boxSizing: 'border-box',
                                padding: '8px 0'
                              }}>
                                {(() => {
                                  const availableContacts = filteredContacts.filter(c => c.id !== contact.id);
                                  
                                  return availableContacts.length > 0 ? (
                                    availableContacts.map((c) => {
                                      const name = getContactName(c);
                                      const isSelected = selectedReportees.includes(name);
                                      return (
                                        <label 
                                          key={c.id} 
                                          className="multi-select-option"
                                          style={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleReportee(name)}
                                            className="multi-select-checkbox"
                                          />
                                          <span className="multi-select-label">
                                            {name}
                                            {c.title && <span className="multi-select-subtitle"> - {c.title}</span>}
                                          </span>
                                        </label>
                                      );
                                    })
                                  ) : (
                                    <div className="no-contacts-message">
                                      <p>No other contacts available in this company.</p>
                                      <p className="help-text">Add more contacts to this company to assign reportees.</p>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="edit-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button 
                                onClick={cancelEditingReportees}
                                disabled={saving}
                                className="cancel-btn"
                              >
                                Cancel
                              </button>
                              <button 
                                onClick={() => saveReportees(contact)}
                                disabled={saving}
                                className="save-btn"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="reportees-display">
                            <div className="reportees-header">
                              <span className="reportees-label">Reportees:</span>
                              <button 
                                onClick={() => startEditingReportees(contact.id, reportees)}
                                className="manage-reportees-btn"
                                title="Manage reportees"
                              >
                                {reportees.length > 0 ? 'Edit' : 'Add'} Reportees
                              </button>
                            </div>
                            {reportees.length > 0 ? (
                              <ul className="buyer-list">
                                {reportees.map((reportee, idx) => (
                                  <li key={idx}>{reportee}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="section-empty">No reportees found</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Team Members */}
                    <div className="buyer-section" style={{ position: 'relative' }}>
                      <div className="section-content">
                        {editingTeamMembers === contact.id ? (
                          <div className="reportees-edit-mode" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            zIndex: 1000,
                            padding: '16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '2px solid #667eea'
                          }}>
                            <div className="multi-select-container" style={{
                              borderRadius: '8px',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: '200px',
                              maxHeight: '400px',
                              overflow: 'hidden',
                              position: 'relative',
                              boxSizing: 'border-box'
                            }}>
                              <div className="multi-select-header" style={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#374151',
                                paddingBottom: '12px',
                                marginBottom: '12px',
                                flexShrink: 0,
                                width: '100%',
                                boxSizing: 'border-box'
                              }}>
                                Select team members for {getContactName(contact)} (contacts with the same manager):
                              </div>
                              <div className="multi-select-options" style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '8px',
                                flex: '1 1 auto',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                minHeight: 0,
                                width: '100%',
                                position: 'relative',
                                padding: '8px 0'
                              }}>
                                {(() => {
                                  const availableContacts = filteredContacts.filter(c => c.id !== contact.id);
                                  return availableContacts.length > 0 ? (
                                    availableContacts.map((c) => {
                                      const name = getContactName(c);
                                      const isSelected = selectedTeamMembers.includes(name);
                                      return (
                                        <label 
                                          key={c.id} 
                                          className="multi-select-option"
                                          style={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleTeamMember(name)}
                                            className="multi-select-checkbox"
                                          />
                                          <span className="multi-select-label">
                                            {name}
                                            {c.title && <span className="multi-select-subtitle"> - {c.title}</span>}
                                          </span>
                                        </label>
                                      );
                                    })
                                  ) : (
                                    <div className="no-contacts-message">
                                      <p>No other contacts available in this company.</p>
                                      <p className="help-text">Add more contacts to this company to assign team members.</p>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="edit-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button 
                                onClick={cancelEditingTeamMembers}
                                disabled={saving}
                                className="cancel-btn"
                              >
                                Cancel
                              </button>
                              <button 
                                onClick={() => saveTeamMembers(contact)}
                                disabled={saving}
                                className="save-btn"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="reportees-display">
                            <div className="reportees-header">
                              <span className="reportees-label">Team Members:</span>
                              <button 
                                onClick={() => {
                                  const contactProps = contact.properties || {};
                                  const manager = contactProps.manager || getContactName(contact);
                                  startEditingTeamMembers(contact.id, teamMembers, manager);
                                }}
                                className="manage-reportees-btn"
                                title="Manage team members"
                              >
                                {teamMembers.length > 0 ? 'Edit' : 'Add'} Team Members
                              </button>
                            </div>
                            {teamMembers.length > 0 ? (
                              <ul className="buyer-list">
                                {teamMembers.map((member) => (
                                  <li key={member.id}>
                                    {getContactName(member)}
                                    {member.title && ` - ${member.title}`}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="section-empty">No team members found</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Last Engagement */}
                    <div className="buyer-section">
                      <div className="section-content">
                        {lastEngagement !== 'No recent engagement' ? (
                          <p className="engagement-text">
                            {lastEngagement}
                          </p>
                        ) : (
                          <p className="section-empty">No last engagement found</p>
                        )}
                      </div>
                    </div>

                    {/* LinkedIn Activity */}
                    <div className="buyer-section">
                      <div className="section-content">
                        <div className="section-header-with-action">
                          <span className="section-label">LinkedIn Activity (Last 3 Months)</span>
                          <button 
                            onClick={() => refreshLinkedInActivity(contact)}
                            disabled={refreshingLinkedIn === contact.id}
                            className="refresh-btn"
                            title="Refresh LinkedIn activity"
                          >
                            <span className={refreshingLinkedIn === contact.id ? "spinner-small" : ""}>⟳</span>
                          </button>
                        </div>
                        {(() => {
                          const activities = getLinkedInActivity(contact);
                          if (activities && activities.length > 0) {
                            // Filter to only show activities from the last 3 months
                            const threeMonthsAgo = new Date();
                            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                            
                            const recentActivities = activities.filter(activity => {
                              if (!activity.publishedAt) return false;
                              const activityDate = new Date(activity.publishedAt);
                              return !isNaN(activityDate.getTime()) && activityDate >= threeMonthsAgo;
                            });
                            
                            // Sort activities by publishedAt to get the latest first (anti-chronological)
                            const sortedActivities = [...recentActivities].sort((a, b) => {
                              const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(0);
                              const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(0);
                              return dateB.getTime() - dateA.getTime(); // Latest to oldest (anti-chronological)
                            });
                            
                            return (
                              <div 
                                className="linkedin-activities-scroll"
                                style={{
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  overflowX: 'hidden'
                                }}
                              >
                                {sortedActivities.map((activity, index) => {
                                  const isObject = typeof activity === 'object';
                                  const summary = isObject ? (activity.summary || activity.text || activity.title) : activity;
                                  const url = isObject ? (activity.url || activity.link) : null;
                                  
                                  return (
                                    <div 
                                      key={index}
                                      className="activity-item activity-item-single-line"
                                      style={{
                                        marginBottom: index < sortedActivities.length - 1 ? '8px' : '0',
                                        padding: '8px 12px',
                                        background: '#f9fafb',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        cursor: url ? 'pointer' : 'default'
                                      }}
                                    >
                                      {url ? (
                                        <a 
                                          href={url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="activity-link activity-link-single-line"
                                          title={summary}
                                          style={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: '#3b82f6',
                                            textDecoration: 'none',
                                            fontSize: '12px',
                                            lineHeight: '1.5'
                                          }}
                                        >
                                          {summary}
                                        </a>
                                      ) : (
                                        <p 
                                          className="activity-text activity-text-single-line"
                                          title={summary}
                                          style={{
                                            margin: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '12px',
                                            color: '#4b5563',
                                            lineHeight: '1.5'
                                          }}
                                        >
                                          {summary}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          return <p className="section-empty">No LinkedIn activity found</p>;
                        })()}
                      </div>
                    </div>

                    {/* News Mentions */}
                    <div className="buyer-section">
                      <div className="section-content">
                        <div className="section-header-with-action">
                          <span className="section-label">News Mentions (Last 3 Months)</span>
                          <button 
                            onClick={() => refreshNewsMentions(contact)}
                            disabled={refreshingNews === contact.id}
                            className="refresh-btn"
                            title="Refresh news mentions"
                          >
                            <span className={refreshingNews === contact.id ? "spinner-small" : ""}>⟳</span>
                          </button>
                        </div>
                        {(() => {
                          const mentions = getNewsMentions(contact);
                          if (mentions && mentions.length > 0) {
                            return (
                              <div className="news-list">
                                {mentions.map((mention, idx) => {
                                  const isObject = typeof mention === 'object';
                                  const summary = isObject ? mention.summary || mention.description || mention.title : mention;
                                  const url = isObject ? mention.url || mention.link : null;
                                  
                                  return (
                                    <div key={idx} className="news-item">
                                      {url ? (
                                        <a 
                                          href={url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="news-link"
                                        >
                                          {summary}
                                        </a>
                                      ) : (
                                        <p className="news-text">{summary}</p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          return <p className="section-empty">No news mentions found</p>;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* LinkedIn ID Missing Popup */}
        {showLinkedInIdPopup && contactNeedingLinkedInId && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
            onClick={() => {
              setShowLinkedInIdPopup(false);
              setContactNeedingLinkedInId(null);
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                  LinkedIn ID Required
                </h3>
                <button
                  onClick={() => {
                    setShowLinkedInIdPopup(false);
                    setContactNeedingLinkedInId(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: 0,
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                To refresh LinkedIn activity for <strong>{getContactName(contactNeedingLinkedInId)}</strong>, 
                please add their LinkedIn ID first in the Contacts screen.
              </p>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                You can enter the LinkedIn URL (e.g., https://www.linkedin.com/in/tanyabisht/).
              </p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowLinkedInIdPopup(false);
                    setContactNeedingLinkedInId(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

RadarBuyerCards.displayName = 'RadarBuyerCards';

export default RadarBuyerCards;
